import { useState } from 'react'
import { Trash2, Loader2 } from 'lucide-react'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'
import { useDepartmentUsers, useRemoveUserFromDepartment } from '@/hooks/useDepartments'
import { showError } from '@/lib/error'
import type { DepartmentUser } from '@/types/department'

// 状态配置：后端 1=正常, 0=禁用
const statusConfig: Record<number, { label: string; variant: 'default' | 'secondary' | 'destructive' }> = {
	1: { label: '正常', variant: 'default' },
	0: { label: '禁用', variant: 'destructive' },
}

interface DepartmentUsersTableProps {
	departmentId: number
	onAddUser: () => void
}

// 删除确认气泡组件
function DeleteConfirmPopover({
	user,
	isPending,
	onConfirm,
}: {
	user: DepartmentUser
	isPending: boolean
	onConfirm: () => void
}) {
	const [open, setOpen] = useState(false)

	const handleConfirm = () => {
		onConfirm()
		setOpen(false)
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="h-8 w-8 text-destructive hover:text-destructive"
					disabled={isPending}
				>
					{isPending ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : (
						<Trash2 className="h-4 w-4" />
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-3" align="end" sideOffset={5}>
				<p className="text-sm mb-3">确定将 <strong>{user.username}</strong> 从部门移除？</p>
				<div className="flex justify-end gap-2">
					<Button variant="outline" size="sm" onClick={() => setOpen(false)}>
						取消
					</Button>
					<Button variant="destructive" size="sm" onClick={handleConfirm}>
						确认
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	)
}

export function DepartmentUsersTable({ departmentId, onAddUser }: DepartmentUsersTableProps) {
	const { data: users, isLoading, isError, error } = useDepartmentUsers(departmentId)
	const removeMutation = useRemoveUserFromDepartment()

	if (isLoading) {
		return (
			<div className="space-y-3">
				{Array.from({ length: 3 }).map((_, i) => (
					<div key={i} className="flex items-center gap-4">
						<Skeleton className="h-10 w-10 rounded-full" />
						<div className="flex-1 space-y-2">
							<Skeleton className="h-4 w-32" />
							<Skeleton className="h-3 w-48" />
						</div>
						<Skeleton className="h-8 w-16" />
					</div>
				))}
			</div>
		)
	}

	if (isError) {
		return (
			<div className="py-8 text-center">
				<p className="text-destructive">加载失败: {(error as Error)?.message}</p>
			</div>
		)
	}

	if (!users || users.length === 0) {
		return (
			<div className="py-8 text-center">
				<p className="text-muted-foreground mb-4">该部门暂无成员</p>
				<Button onClick={onAddUser} size="sm">
					添加成员
				</Button>
			</div>
		)
	}

	return (
		<div className="border rounded-md">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>用户名</TableHead>
						<TableHead>昵称</TableHead>
						<TableHead>邮箱</TableHead>
						<TableHead>手机号</TableHead>
						<TableHead>状态</TableHead>
						<TableHead>创建时间</TableHead>
						<TableHead className="text-right">操作</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{users.map((user) => {
						const statusInfo = statusConfig[user.status] || { label: '未知', variant: 'secondary' as const }
						return (
							<TableRow key={user.id}>
								<TableCell className="font-medium">{user.username}</TableCell>
								<TableCell>{user.nickname || '-'}</TableCell>
								<TableCell>{user.email}</TableCell>
								<TableCell>{user.phone || '-'}</TableCell>
								<TableCell>
									<Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
								</TableCell>
								<TableCell className="text-muted-foreground text-sm">
									{user.created_at}
								</TableCell>
								<TableCell className="text-right">
									<DeleteConfirmPopover
										user={user}
										isPending={removeMutation.isPending}
										onConfirm={() =>
											removeMutation.mutate(
												{ departmentId, userId: user.id },
												{ onError: (error) => showError(error, '移除成员失败') }
											)
										}
									/>
								</TableCell>
							</TableRow>
						)
					})}
				</TableBody>
			</Table>
		</div>
	)
}
